/**
 * One schema per request shape, used twice: Fastify validates with it, and
 * react-hook-form checks the same rules in the browser. Validation on both
 * sides without two definitions that can drift apart.
 */
import { z } from "zod";
import { FieldChecks } from "./field-checks.js";
export const UserId = {
    schema: z.uuid().brand(),
};
export const Email = {
    /** The longest address RFC 5321 allows. */
    MAX_LENGTH: 254,
    schema: z.string().trim().toLowerCase().pipe(z.email("Enter a valid email address.").max(254)).brand(),
};
/**
 * A password a new account is allowed to have: long enough, and not a guess.
 * NIST SP 800-63B: length and a breach check beat composition rules. A short
 * denylist stands in for the breach corpus at this scale.
 */
export const Password = {
    MIN_LENGTH: 12,
    MAX_LENGTH: 128,
    COMMON: new Set(["password123", "123456789012", "qwertyuiop12", "iloveshopping", "letmeinplease", "administrator"]),
    schema: z
        .string()
        .min(12, "Use at least 12 characters.")
        .max(128, "Use at most 128 characters.")
        .refine((password) => !Password.COMMON.has(password.toLowerCase()), "That password is too easy to guess."),
    /** The browser-only confirmation field and the check that it matches. */
    confirmation: z.string().min(1, "Repeat the password."),
    matches: (values) => values.password === values.confirmPassword,
    mismatch: {
        message: "The two passwords do not match.",
        path: ["confirmPassword"],
        when: FieldChecks.runWhenValid("password", "confirmPassword"),
    },
};
export const TotpCode = {
    schema: z
        .string()
        .trim()
        .regex(/^\d{6}$/, "Enter the 6-digit code from your authenticator app.")
        .brand(),
};
export const CaptchaToken = {
    schema: z.string().min(1, "Complete the CAPTCHA to continue.").brand(),
};
export const RegisterInput = {
    schema: z.object({
        email: Email.schema,
        password: Password.schema,
        captchaToken: CaptchaToken.schema,
    }),
};
export const LoginInput = {
    schema: z.object({
        email: Email.schema,
        password: z.string().min(1, "Enter your password."),
        /** Needed once the account's sign-in attempts have run out. */
        captchaToken: CaptchaToken.schema.optional(),
    }),
};
export const ChallengeToken = {
    schema: z.string().min(1).brand(),
};
export const AccessToken = {
    schema: z.string().min(1).brand(),
};
export const ResetToken = {
    schema: z.string().min(1).brand(),
};
export const TwoFactorLoginInput = {
    schema: z.object({
        challengeToken: ChallengeToken.schema.optional(),
        code: TotpCode.schema,
    }),
};
export const ForgotPasswordInput = {
    schema: z.object({ email: Email.schema }),
};
export const ResetPasswordInput = {
    schema: z.object({ token: ResetToken.schema, password: Password.schema }),
};
export const ChangePasswordInput = {
    schema: z.object({
        currentPassword: z.string().min(1).optional(),
        password: Password.schema,
    }),
};
export const TwoFactorCodeInput = {
    schema: z.object({ code: TotpCode.schema }),
};
export const EnableTwoFactorInput = {
    schema: TwoFactorCodeInput.schema.extend({ currentPassword: z.string().min(1).optional() }),
    /** The form of an account with a password, which has to give it. */
    withPassword: TwoFactorCodeInput.schema.extend({ currentPassword: z.string().min(1, "Enter your current password.") }),
};
export const RegisterForm = {
    schema: RegisterInput.schema.extend({ confirmPassword: Password.confirmation }).refine(Password.matches, Password.mismatch),
};
export const ResetPasswordForm = {
    schema: ResetPasswordInput.schema
        .omit({ token: true })
        .extend({ confirmPassword: Password.confirmation })
        .refine(Password.matches, Password.mismatch),
};
export const PasswordForm = {
    change: ChangePasswordInput.schema
        .extend({
        currentPassword: z.string().min(1, "Enter your current password."),
        confirmPassword: Password.confirmation,
    })
        .refine(Password.matches, Password.mismatch),
    set: ChangePasswordInput.schema
        .extend({ currentPassword: z.string(), confirmPassword: Password.confirmation })
        .refine(Password.matches, Password.mismatch),
};
export const OAuthProvider = {
    NAMES: ["google", "github"],
    /** A provider name arriving from a client, which may be anything at all. */
    schema: z.enum(["google", "github"]),
    /** How each provider spells its own name on a button. */
    label: { google: "Google", github: "GitHub" },
};
export const OtpauthUri = {
    schema: z.string().startsWith("otpauth://").brand(),
};
//# sourceMappingURL=auth.js.map